import { getAmpConfig } from 'shared/config/amp-config';
import {
  AssignmentContent,
  Brand,
  CardstackAssignment,
} from 'shared/api/assignment-service';
import { LessonContent } from 'shared/api/class-stats';
import { REVISION_ALIAS_21_22 } from 'shared/config/constants';
import { Asset } from 'shared/api/retail';
import { MCLASS_ASSET } from 'shared/config/entitelment-constants';
import { dateService } from 'shared/lib/date';

import { PROGRAMS_ASSETS } from './assets';

export const getProgramLink = (
  programAssetId: string,
  userPrograms?: Asset[],
): string | undefined => {
  if (programAssetId === MCLASS_ASSET && userPrograms) {
    const mClassProgram = userPrograms.find(
      (program) => program.businessKey === programAssetId,
    );
    return mClassProgram?.metadata.dashboard.appUrl;
  }

  const PROGRAM_LINKS = {
    [PROGRAMS_ASSETS.BoostReadingAqua]: getAmpConfig().amplifyReading,
    [PROGRAMS_ASSETS.BoostReading]: getAmpConfig().amplifyReading,
    [PROGRAMS_ASSETS.BoostReadingGrade3]: getAmpConfig().amplifyReading,
    [PROGRAMS_ASSETS.BoostReadingGrade4]: getAmpConfig().amplifyReading,
    [PROGRAMS_ASSETS.BoostReadingGrade5]: getAmpConfig().amplifyReading,
    [PROGRAMS_ASSETS.BoostTexasElementary]: getAmpConfig().amplifyReading,
    [PROGRAMS_ASSETS.BoostLectura]: getAmpConfig().amplifyReading,
    [PROGRAMS_ASSETS.BoostCloseReading]: getAmpConfig().amplifyCloseReading,
    [PROGRAMS_ASSETS.BoostReadingTexasMiddleSchool]:
      getAmpConfig().amplifyCloseReading,
    [PROGRAMS_ASSETS.BoostReadingMiddleSchool]:
      getAmpConfig().amplifyCloseReading,
    [PROGRAMS_ASSETS.Desmos]: getAmpConfig().desmosTeacher,
    [PROGRAMS_ASSETS.DesmosNYC]: getAmpConfig().desmosTeacher,
    [PROGRAMS_ASSETS.mClassUnifiedAdminReporting]: `${
      getAmpConfig().amplifyMClass
    }/reading-classroom-reports/`,
    [PROGRAMS_ASSETS.TexasELP]: getAmpConfig().elementaryLiteracyProgram,
    [PROGRAMS_ASSETS.TexasELPSGrade1]: getAmpConfig().elementaryLiteracyProgram,
    [PROGRAMS_ASSETS.TexasELPSGrade2]: getAmpConfig().elementaryLiteracyProgram,
    [PROGRAMS_ASSETS.TexasELPSGrade3]: getAmpConfig().elementaryLiteracyProgram,
    [PROGRAMS_ASSETS.TexasELPSGrade4]: getAmpConfig().elementaryLiteracyProgram,
    [PROGRAMS_ASSETS.TexasELPSGrade5]: getAmpConfig().elementaryLiteracyProgram,
  };

  return PROGRAM_LINKS[programAssetId];
};

export const getDomainLink = (domain: string, programAssetId?: string) => {
  if (programAssetId) {
    const programLink = getProgramLink(programAssetId);
    if (programLink) {
      return programLink;
    }
  }
  return `${getAmpConfig().apps}/curriculum/#/yearoverview?subject=${domain}`;
};

const getCustomClassroomLink = (customContentId: string) =>
  `${getAmpConfig().apps}/classroom/#/custom/lesson/${customContentId}`;

const getRevisionAlias = (unitKey: string) => {
  const hasRevisionAlias = unitKey.match(/(\d{4}-\d{4}|latest)/);

  return hasRevisionAlias ? '' : `:${REVISION_ALIAS_21_22}`;
};

const getClassroomLink = (
  unitKey: string,
  lessonKey: string,
  chapterKey: string,
  unitRevisionAlias?: string,
) => {
  const revisionAlias = unitRevisionAlias
    ? `:${unitRevisionAlias}`
    : getRevisionAlias(unitKey);

  return `${
    getAmpConfig().apps
  }/classroom/#/unit/${unitKey}${revisionAlias}/lesson/${lessonKey}/chapter/${chapterKey}`;
};

export const getClassroomDashboardLink = (
  content: AssignmentContent | LessonContent,
  classBk: string,
) => {
  const dashboardQueryParam = 'modal=classstatus';
  const classIdQueryParam = `&orchestrationDashboardClassId=${classBk}`;

  if (content.brand !== Brand.REGULAR && content.customContentId) {
    return `${getCustomClassroomLink(
      content.customContentId,
    )}?${dashboardQueryParam}`;
  }

  return content.unitKey && content.lessonKey && content.chapterKey
    ? `${getClassroomLink(
        content.unitKey,
        content.lessonKey,
        content.chapterKey,
        content.unitRevisionAlias,
      )}?${dashboardQueryParam}${classIdQueryParam}`
    : null;
};

export const getClassworkClassActivitiesLink = (
  cardstackAssignment: CardstackAssignment,
) => {
  const { classBks, content } = cardstackAssignment;
  const { unitKey, unitRevisionAlias, chapterKey, lessonKey } = content[0];

  const classworkLinkBase = `${getAmpConfig().apps}/classwork/#/classes/${
    classBks[0]
  }`;
  const defaultClassActivitiesLink = `${classworkLinkBase}/activities`;

  const hasRequiredData =
    unitKey && unitRevisionAlias && chapterKey && lessonKey;

  const currentYear = dateService.getDayJSObject().year();
  const isUnitRevisionAliasValid = unitRevisionAlias?.includes(
    currentYear.toString(),
  );

  if (!hasRequiredData || !isUnitRevisionAliasValid)
    return defaultClassActivitiesLink;

  const unitPath = `unit/${unitKey}:${unitRevisionAlias}`;
  const chapterPath = `chapter/${chapterKey}`;
  const lessonPath = `lesson/${lessonKey}`;

  return `${classworkLinkBase}/${unitPath}/${chapterPath}/${lessonPath}/activities`;
};

export const getCurriculumLessonLink = (
  unitKey: string,
  lessonKey: string,
  chapterKey: string,
) =>
  `${
    getAmpConfig().apps
  }/curriculum/#/unit/${unitKey}/lesson/${lessonKey}/chapter/${chapterKey}`;

export const getLessonLink = (content: AssignmentContent | LessonContent) => {
  if (content.brand === Brand.CUSTOM && content.customContentId) {
    return getCustomClassroomLink(content.customContentId);
  }

  return content.unitKey && content.lessonKey && content.chapterKey
    ? getCurriculumLessonLink(
        content.unitKey,
        content.lessonKey,
        content.chapterKey,
      )
    : undefined;
};

export const fixMissingHttps = (url: string) => {
  const rootURLPattern = /^(\w|\d|-)+\./gim;
  return rootURLPattern.test(url) ? `https://${url}` : url;
};

export const isBynderLink = (link: string) => {
  const bynderAssetsRootPath = `${getAmpConfig().assets}/m/`;

  return link.startsWith(bynderAssetsRootPath);
};
